import { Col, Form, Row } from "react-bootstrap";
import { Required } from "components/Required";
import { Controller, useForm } from "react-hook-form";
import { DropdownSelect } from "components/DropdownSelect";
import { CustomInput } from "components/CustomInput";
import { useMaskInput } from "hooks/useMaskInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { criarLoteSchema } from "../../../validate/criarLoteSchema";
import { useEffect, useState } from "react";
import { useFornecedores } from "../../../../Fornecedor/hooks/useFornecedores";
import { ICriarLote } from "../../../interface/ICriarLote";
import FileUpload from "components/UploadArquivos";
import temaPadrao from "_config/temas/estilo/base";
import {
  Container,
  ContainerForm,
  ContainerHeader,
  ContainerInstrucoes,
  HeaderButtonsContainer,
  Title,
} from "./style";
import { Informacao } from "components/Informacao";
import { FaExclamationCircle, FaPlus } from "react-icons/fa";
import { PrimaryButton } from "components/Button/style";

interface IProps {
  dadosCriarLote: ICriarLote;
  onClickCancelar: () => void;
  setTela: (Tela: "vincular" | "resumo") => void;
  onClickSalvar: () => void;
}

export function FormCriarLote({
  dadosCriarLote,
  onClickCancelar,
  setTela,
  onClickSalvar,
}: IProps) {
  const { optionsFornecedores, gerarOptionsFornecedores } = useFornecedores({
    semFiltroStatus: true,
  });
  const [tipoLancamentoDisabled, setTipoLancamentoDisabled] =
    useState<boolean>(false);
  const [fornecedorDisabled, setFornecedorDisabled] = useState<boolean>(false);
  const [fornecedorEscolhido, setFornecedorEscolhido] = useState<number>(
    dadosCriarLote.fornecedor,
  );

  const { mInputCoin } = useMaskInput();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(criarLoteSchema),
  });

  useEffect(() => {
    reset(dadosCriarLote);
    setValue("arquivo", dadosCriarLote.arquivo);
    setValue("tipoLancamento", "C");
    setTipoLancamentoDisabled(true);
    gerarOptionsFornecedores();
  }, []);

  useEffect(() => {
    setFornecedorEscolhido(dadosCriarLote.fornecedor);
  }, [optionsFornecedores, setFornecedorEscolhido]);

  useEffect(() => {
    setValue("fornecedor", fornecedorEscolhido);

    if (fornecedorEscolhido) {
      setFornecedorDisabled(true);
    }
  }, [fornecedorEscolhido, setFornecedorDisabled]);

  return (
    <Container>
      <ContainerHeader>
        <Title>Criar novo lote de pagamentos</Title>
        <HeaderButtonsContainer>
          <PrimaryButton
            variante="primary"
            outline
            onClick={() => onClickCancelar()}
          >
            Cancelar
          </PrimaryButton>
          <PrimaryButton
            variante="primary"
            outline
            onClick={() => setTela("vincular")}
          >
            <FaPlus /> Adicionar custos
          </PrimaryButton>
          <PrimaryButton variante="primary" onClick={() => onClickSalvar()}>
            Salvar lote
          </PrimaryButton>
        </HeaderButtonsContainer>
      </ContainerHeader>
      <hr />
      <ContainerInstrucoes>
        Preencha os campos abaixo com os dados do recibo ou nota fiscal emitida
        pelo fornecedor.
      </ContainerInstrucoes>
      <Informacao
        mensagem="Após salvar o lote, os custos informados nesta tela serão vinculados aos respectivos processos."
        type="atencao"
        showBotaoFechar={false}
        showLabel={false}
        icone={<FaExclamationCircle />}
      />
      <ContainerForm>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>
                Tipo de lançamento <Required />
              </Form.Label>
              <Controller
                control={control}
                name="tipoLancamento"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={[{ value: "C", label: "Custos Processo" }]}
                    selectedValue={"C"}
                    onSelect={onChange}
                    disabled={tipoLancamentoDisabled}
                    mensagemErro={errors.tipoLancamento?.message}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                Fornecedor <Required />
              </Form.Label>
              <Controller
                control={control}
                name="fornecedor"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={optionsFornecedores}
                    title=""
                    selectedValue={dadosCriarLote.fornecedor}
                    onSelect={onChange}
                    searchable
                    mensagemErro={errors.fornecedor?.message}
                    disabled={fornecedorDisabled}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                Nº Recibo / NF <Required />
              </Form.Label>
              <Controller
                control={control}
                name="numeroNfRecibo"
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    placeholder="Informe o nº do recibo ou NF emitido pelo fornecedor"
                    style={{ height: "2.2rem" }}
                    onChange={onChange}
                    value={value}
                    mensagemErro={errors.numeroNfRecibo?.message}
                    isInvalid={!!errors.numeroNfRecibo?.message}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                Valor total do recibo / NF <Required />
              </Form.Label>
              <Controller
                control={control}
                name="valorNfRecibo"
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    placeholder="Informe o valor do recibo ou NF emitido pelo fornecedor"
                    style={{ height: "2.2rem" }}
                    onChange={(e: any) =>
                      onChange(
                        mInputCoin({
                          ...e,
                          target: {
                            ...e.target,
                            value: e.target.value.replace(/[^0-9.]/g, ""),
                          },
                        }),
                      )
                    }
                    value={value}
                    mensagemErro={errors.valorNfRecibo?.message}
                    isInvalid={!!errors.valorNfRecibo?.message}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label
                style={{ color: temaPadrao.colors.neutral.neutral70 }}
              >
                Upload do arquivo <Required />
              </Form.Label>
              <Controller
                name="arquivo"
                control={control}
                render={({ field: { onChange } }) => (
                  <FileUpload
                    onFileUpload={(file) => {
                      onChange(file);
                    }}
                    mensagemErro={errors.arquivo?.message}
                    comMaxWidth={false}
                    inputAccept=""
                    fileInicial={dadosCriarLote.arquivo}
                  />
                )}
              />
            </Form.Group>
          </Col>
        </Row>
      </ContainerForm>
    </Container>
  );
}
